/**
 * The profile fields an owner may write through updateProfile, and the
 * profiles column each one lands in. Shared by both adapters so the mock
 * accepts exactly what prod persists: a field missing here is silently
 * dropped by the Supabase adapter, and the mock must drop it too.
 *
 * Not here on purpose: email (DEV-803, set by the profiles edge function from
 * a Privy-verified account) and the image fields (setProfileImage /
 * clearProfileImage own avatar uploads and banner_path).
 */

import type { UserProfile } from "@/lib/types";

export const PROFILE_UPDATE_COLUMNS = {
  handle: "handle",
  displayName: "display_name",
  avatarUrl: "avatar_url",
  bio: "bio",
  skills: "skills",
  walletAddress: "wallet_address",
  rolePreference: "role_preference",
  // DEV-783 step 9. The one thing the payout screen's toggle writes.
  recurringMatch: "recurring_match",
  activityLevel: "activity_level",
  needs: "needs",
  locationCity: "location_city",
  locationCountry: "location_country",
  preferredLanguage: "preferred_language",
} as const satisfies Partial<Record<keyof UserProfile, string>>;

type WritableProfileField = keyof typeof PROFILE_UPDATE_COLUMNS;

const WRITABLE_FIELDS = Object.keys(PROFILE_UPDATE_COLUMNS) as WritableProfileField[];

/** The writable subset of `data`, camelCase; undefined means "not sent". */
export function pickWritableProfileFields(data: Partial<UserProfile>): Partial<UserProfile> {
  const picked: Partial<Record<WritableProfileField, unknown>> = {};
  for (const field of WRITABLE_FIELDS) {
    if (data[field] !== undefined) picked[field] = data[field];
  }
  return picked as Partial<UserProfile>;
}

/** The same subset keyed by profiles column, ready for `.update()`. */
export function toProfileUpdateRow(data: Partial<UserProfile>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const field of WRITABLE_FIELDS) {
    if (data[field] !== undefined) row[PROFILE_UPDATE_COLUMNS[field]] = data[field];
  }
  return row;
}
